import { Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import { useTranslation } from 'react-i18next';

const HERO_IMG = 'https://readdy.ai/api/search-image?query=Professional%20surrogacy%20service%20warm%20fertility%20clinic%20interior%2C%20happy%20couple%20with%20caring%20doctor%20advisor%2C%20champagne%20gold%20and%20cream%20tones%2C%20soft%20natural%20light%20streaming%20through%20windows%2C%20hopeful%20emotional%20atmosphere%2C%20medical%20consultation%20room&width=1600&height=700&seq=scy-hero-2026&orientation=landscape';

const STEP_IMGS = [
  'https://readdy.ai/api/search-image?query=Couple%20initial%20consultation%20fertility%20doctor%2C%20warm%20professional%20clinic%20office%2C%20two%20people%20discussing%20medical%20plan%20with%20advisor%2C%20champagne%20cream%20tones%20soft%20light&width=600&height=400&seq=scy-step-01-2026&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Surrogate%20mother%20profile%20selection%20database%2C%20doctor%20reviewing%20candidate%20files%20on%20tablet%2C%20warm%20clinic%2C%20champagne%20tones%2C%20careful%20professional%20selection%20process&width=600&height=400&seq=scy-step-02-2026&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Complete%20pre-natal%20medical%20screening%20fertility%20clinic%2C%20doctor%20reviewing%20comprehensive%20health%20check%20report%20with%20female%20patient%2C%20bright%20clean%20consultation%20room%2C%20champagne%20and%20cream%20tones%2C%20reassuring%20professional%20atmosphere%2C%20clear%20bright%20lighting&width=600&height=400&seq=scy-step-03-2027&orientation=landscape&nocache=true',
  'https://readdy.ai/api/search-image?query=IVF%20embryo%20transfer%20medical%20procedure%20fertility%20clinic%2C%20doctor%20embryologist%20microscope%20laboratory%2C%20clean%20white%20high-tech%20lab%2C%20professional%20precision%20medical%20science&width=600&height=400&seq=scy-step-04-2026&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Pregnant%20surrogate%20mother%20prenatal%20care%20check%20up%20ultrasound%2C%20warm%20clinic%20room%2C%20caring%20doctor%20monitoring%20pregnancy%2C%20soft%20champagne%20cream%20light&width=600&height=400&seq=scy-step-05-2026&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Newborn%20baby%20hospital%20birth%20parents%20holding%20baby%2C%20emotional%20joyful%20moment%20maternity%20ward%2C%20warm%20soft%20lighting%20champagne%20cream%20tones%2C%20happy%20new%20parents&width=600&height=400&seq=scy-step-06-2026&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Passport%20birth%20certificate%20legal%20documents%20baby%20family%2C%20official%20legal%20papers%20stamped%2C%20attorney%20office%2C%20returning%20home%20family%20travel%20document%20preparation&width=600&height=400&seq=scy-step-07-2026&orientation=landscape',
];

const ITEM_ICONS = ['ri-user-search-line', 'ri-heart-pulse-line', 'ri-hospital-line', 'ri-shopping-bag-line', 'ri-snowflake-line', 'ri-video-chat-line', 'ri-nurse-line', 'ri-file-paper-line', 'ri-dna-line', 'ri-passport-line', 'ri-car-line', 'ri-hotel-line'];

const surrogacyJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: '单助孕 - RTB吉尔吉斯斯坦试管代孕中心',
    description: '已有胚胎的代孕服务，惠享$63,600/安心$71,600，7步专业流程，从志愿者筛选到宝宝回国，法律双亲权保障。',
    url: `${SITE_URL}/surrogacy`,
  },
];

export default function SurrogacyDetailPage() {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const steps = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const n = i + 1;
    return {
      num: `0${n}`,
      title: t(`scy_step${n}_title`),
      duration: t(`scy_step${n}_dur`),
      desc: t(`scy_step${n}_desc`),
      points: [t(`scy_step${n}_p1`), t(`scy_step${n}_p2`), t(`scy_step${n}_p3`), t(`scy_step${n}_p4`)],
    };
  }), [t]);

  const includedItems = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const n = i + 1;
    return {
      icon: ITEM_ICONS[i],
      title: t(`scy_item${n}_title`),
      desc: t(`scy_item${n}_desc`),
    };
  }), [t]);

  const extraItems = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const n = i + 1;
    return {
      title: t(`scy_extra${n}_title`),
      price: t(`scy_extra${n}_price`),
      desc: t(`scy_extra${n}_desc`),
    };
  }), [t]);

  const pricingPlans = useMemo(() => [
    {
      level: t('scy_plan_economy'),
      price: '$63,600',
      unit: 'USD',
      badge: t('scy_plan_economy_badge'),
      desc: t('scy_plan_economy_desc'),
      items: Array.from({ length: 11 }, (_, i) => t(`scy_plan_economy_item${i + 1}`)),
      highlight: false,
    },
    {
      level: t('scy_plan_premium'),
      price: '$71,600',
      unit: 'USD',
      badge: t('scy_plan_premium_badge'),
      desc: t('scy_plan_premium_desc'),
      items: Array.from({ length: 11 }, (_, i) => t(`scy_plan_premium_item${i + 1}`)),
      highlight: true,
    },
  ], [t]);

  const planNoteIncItems = useMemo(() => Array.from({ length: 7 }, (_, i) => t(`scy_plan_inc_item${i + 1}`)), [t]);
  const planNoteExtraItems = useMemo(() => Array.from({ length: 6 }, (_, i) => t(`scy_plan_extra_item${i + 1}`)), [t]);

  return (
    <>
      <SeoHead
        title="单助孕｜惠享$63,600·安心$71,600 - RTB吉尔吉斯斯坦试管代孕中心"
        description="RTB吉尔吉斯斯坦单助孕服务：已有胚胎的代孕方案，惠享$63,600/安心$71,600，7步全流程，志愿者严格筛选，法律双亲权100%保障。"
        keywords="单助孕,吉尔吉斯斯坦代孕,已有胚胎代孕,代孕费用,代孕流程"
        canonical="/surrogacy"
        jsonLd={surrogacyJsonLd}
      />
      <main className="pt-16 md:pt-20">
        {/* Hero */}
        <section className="relative h-[420px] md:h-[580px] flex items-center overflow-hidden">
          <img src={HERO_IMG} alt="单助孕" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2137]/80 via-[#0d2137]/50 to-transparent"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-4">
                <Link to="/services" className="text-[#c9a96e] text-xs hover:underline cursor-pointer">{t('ivf_breadcrumb_svc')}</Link>
                <i className="ri-arrow-right-s-line text-[#c9a96e] text-xs"></i>
                <span className="text-white/70 text-xs">{t('scy_breadcrumb')}</span>
              </div>
              <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-3">{t('scy_label')}</div>
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">{t('scy_page_title')}</h1>
              <p className="text-white/80 text-sm md:text-base leading-relaxed mb-8">
                {t('scy_hero_desc')}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-7 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm">
                  <i className="ri-calendar-line"></i> {t('ivf_cta_consult')}
                </Link>
                <a href="https://wa.me/8618513885468" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium px-7 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm backdrop-blur-sm">
                  <i className="ri-whatsapp-line"></i> {t('ivf_cta_whatsapp')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key stats */}
        <section className="bg-[#1a3a5c] py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { num: '$63,600', labelKey: 'scy_stat_price1' },
                { num: '$71,600', labelKey: 'scy_stat_price2' },
                { num: '500+', labelKey: 'scy_stat_cases' },
                { num: '100%', labelKey: 'scy_stat_legal' },
              ].map((s) => (
                <div key={s.labelKey}>
                  <div className="text-2xl md:text-3xl font-bold text-[#c9a96e] mb-1">{s.num}</div>
                  <div className="text-white/70 text-xs">{t(s.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-24 bg-[#f8f5f0]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <div className="text-[#c9a96e] text-xs font-medium tracking-widest uppercase mb-2">{t('scy_process_label')}</div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('scy_process_title')}</h2>
              <p className="text-gray-500 text-sm">{t('scy_process_sub')}</p>
            </div>

            <div className="mb-10 relative">
              <div className="absolute top-5 left-0 right-0 h-0.5 bg-gradient-to-r from-[#c9a96e] to-[#1a3a5c]/20 hidden md:block"></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {steps.map((s, i) => (
                  <button key={i} onClick={() => setActiveStep(i)} className={`text-left p-4 rounded-xl transition-all cursor-pointer relative ${activeStep === i ? 'bg-[#1a3a5c] text-white' : 'bg-white hover:bg-[#f0e8d8]'}`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full mb-3 text-sm font-bold ${activeStep === i ? 'bg-[#c9a96e] text-white' : 'bg-[#f8f5f0] text-[#c9a96e]'}`}>{s.num}</div>
                    <div className={`text-xs font-semibold mb-1 ${activeStep === i ? 'text-white' : 'text-[#1a3a5c]'}`}>{s.title}</div>
                    <div className={`text-xs ${activeStep === i ? 'text-white/60' : 'text-gray-400'}`}>{s.duration}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="h-64 md:h-80 lg:h-auto overflow-hidden">
                  <img src={STEP_IMGS[activeStep]} alt={steps[activeStep].title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl font-bold text-[#c9a96e]/30">{steps[activeStep].num}</span>
                    <div>
                      <div className="text-xs text-[#c9a96e] font-medium mb-0.5">{steps[activeStep].duration}</div>
                      <h3 className="text-xl md:text-2xl font-bold text-[#1a3a5c]">{steps[activeStep].title}</h3>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">{steps[activeStep].desc}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {steps[activeStep].points.map((p) => (
                      <div key={p} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#c9a96e] shrink-0 text-base"></i>{p}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-8">
                    <button onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))} disabled={activeStep === 0} className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:border-[#1a3a5c] hover:text-[#1a3a5c] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <button onClick={() => setActiveStep((prev) => Math.min(steps.length - 1, prev + 1))} disabled={activeStep === steps.length - 1} className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1a3a5c] text-white hover:bg-[#2a5a8c] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                    <span className="ml-auto text-xs text-gray-400 self-center">{activeStep + 1} / {steps.length}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Included items */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('scy_included_title')}</h2>
              <p className="text-gray-500 text-sm">{t('scy_included_sub')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {includedItems.map((item) => (
                <div key={item.title} className="p-6 rounded-2xl bg-[#f8f5f0] hover:bg-[#f0e8d8] transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1a3a5c]/8 text-[#c9a96e] mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1a3a5c] text-sm mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Extra fees */}
        <section className="py-16 bg-[#f8f5f0]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('scy_extra_title')}</h2>
              <p className="text-gray-500 text-sm">{t('scy_extra_sub')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {extraItems.map((item) => (
                <div key={item.title} className="bg-white rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#f8f5f0] text-[#c9a96e] shrink-0">
                    <i className="ri-price-tag-3-line text-lg"></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-[#1a3a5c] text-sm">{item.title}</h3>
                      <span className="text-xs text-[#c9a96e] font-semibold">{item.price}</span>
                    </div>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing comparison */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <div className="text-[#c9a96e] text-xs font-medium tracking-widest uppercase mb-2">{t('scy_plan_label')}</div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('scy_plan_title')}</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">{t('scy_plan_sub')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-10">
              {pricingPlans.map((plan) => (
                <div
                  key={plan.level}
                  className={`relative rounded-2xl overflow-hidden border-2 transition-all ${plan.highlight ? 'border-[#c9a96e] bg-[#1a3a5c]' : 'border-gray-100 bg-[#f8f5f0]'}`}
                >
                  {plan.badge && (
                    <div className="absolute top-4 right-4 bg-[#c9a96e] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {plan.badge}
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                    <div className="text-xs font-medium tracking-widest uppercase mb-2 text-[#c9a96e]">{plan.level}</div>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className={`text-3xl font-bold ${plan.highlight ? 'text-white' : 'text-[#1a3a5c]'}`}>{plan.price}</span>
                      <span className={`text-sm ${plan.highlight ? 'text-white/70' : 'text-gray-400'}`}>{plan.unit}</span>
                    </div>
                    <p className={`text-xs mb-6 ${plan.highlight ? 'text-white/60' : 'text-gray-500'}`}>{plan.desc}</p>
                    <div className="space-y-2.5 mb-7">
                      {plan.items.map((item) => (
                        <div key={item} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-[#c9a96e] shrink-0 mt-0.5"></i>
                          <span className={`text-xs ${plan.highlight ? 'text-white/80' : 'text-gray-600'}`}>{item}</span>
                        </div>
                      ))}
                    </div>
                    <a href="/contact" className={`block text-center py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${plan.highlight ? 'bg-[#c9a96e] hover:bg-[#b8944f] text-white' : 'bg-[#1a3a5c] hover:bg-[#2a4a6c] text-white'}`}>
                      {t('scy_plan_btn')}
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-[#f8f5f0] rounded-2xl p-6 md:p-8">
              <div className="flex items-start gap-3 mb-4">
                <i className="ri-information-line text-[#c9a96e] text-xl shrink-0 mt-0.5"></i>
                <h3 className="font-bold text-[#1a3a5c] text-sm">{t('scy_plan_note_title')}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-semibold text-[#1a3a5c] mb-3">{t('scy_plan_inc_title')}</div>
                  <div className="space-y-1.5">
                    {planNoteIncItems.map((i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-green-500"></i>{i}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#1a3a5c] mb-3">{t('scy_plan_extra_title')}</div>
                  <div className="space-y-1.5">
                    {planNoteExtraItems.map((i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-information-line text-[#c9a96e]"></i>{i}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-5 pt-5 border-t border-gray-200 text-xs text-gray-400">
                {t('scy_plan_note')}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#1a3a5c]">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('scy_cta_title')}</h2>
            <p className="text-white/70 text-sm mb-8">{t('scy_cta_desc')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-8 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-calendar-check-line"></i> {t('ivf_cta_consult')}
              </Link>
              <a href="https://wa.me/8618513885468" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium px-8 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-whatsapp-line"></i> {t('ivf_cta_whatsapp')}
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}